import { useState } from 'react';

/**
 * Column addition, on screen, laid out the way it is laid out on paper.
 *
 * The whole point is that this looks like her exercise book: the numbers
 * stacked, a box above each column for the carried digit, and a row of cells
 * underneath for the answer. A child who learns the method on paper and then
 * meets a horizontal "167 + 167 = ?" has to translate before they can start.
 *
 * Two decisions worth keeping:
 *
 *  1. **The carry boxes are never marked.** They are working, not answer -
 *     exactly as on paper, where a teacher marks the result and not the little
 *     ones above the line. A child who reaches the right total by a route of
 *     their own has not made a mistake, and marking their scratch would teach
 *     them there is only one permitted way to think.
 *  2. **Digits are entered right to left**, because that is the direction the
 *     method runs. The ones cell takes the first tap.
 *
 * A CSS grid holds the columns rather than a monospace font, so nothing can
 * drift out of alignment - misreading a column is the whole difficulty here.
 */

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];

interface ColumnSumProps {
  top: number;
  bottom: number;
  answerCells: number;
  disabled: boolean;
  onSubmit: (value: string) => void;
  /** Subtraction stacks exactly the same way, and borrows in the same boxes. */
  operator?: '+' | '−';
}

export function ColumnSum({
  top,
  bottom,
  answerCells,
  disabled,
  onSubmit,
  operator = '+',
}: ColumnSumProps) {
  // Both arrays are indexed left to right so they line up with the grid; the
  // rightmost cell is the ones column.
  const [answer, setAnswer] = useState<string[]>(() => Array(answerCells).fill(''));
  const [carries, setCarries] = useState<string[]>(() => Array(answerCells - 1).fill(''));
  const [target, setTarget] = useState<{ row: 'answer' | 'carry'; index: number }>({
    row: 'answer',
    index: answerCells - 1,
  });

  const digitsOf = (n: number) => String(n).padStart(answerCells, ' ').split('');

  const typed = answer.join('').trim();
  const canSubmit = typed.length > 0 && !disabled;

  const press = (key: string) => {
    if (disabled) return;
    if (target.row === 'answer') {
      const next = [...answer];
      next[target.index] = key;
      setAnswer(next);
      // Walk left, the way the method runs.
      if (target.index > 0) setTarget({ row: 'answer', index: target.index - 1 });
    } else {
      const next = [...carries];
      next[target.index] = key;
      setCarries(next);
    }
  };

  const rub = () => {
    if (disabled) return;
    if (target.row === 'answer') {
      const next = [...answer];
      if (next[target.index]) {
        next[target.index] = '';
      } else if (target.index < answerCells - 1) {
        next[target.index + 1] = '';
        setTarget({ row: 'answer', index: target.index + 1 });
      }
      setAnswer(next);
    } else {
      const next = [...carries];
      next[target.index] = '';
      setCarries(next);
    }
  };

  const submit = () => {
    if (!canSubmit) return;
    // Blanks are leading spaces the child chose not to use, so the value is the
    // digits they actually wrote. "0334" and "334" must mean the same thing.
    const value = answer.map((d) => d || '').join('');
    onSubmit(String(Number(value)));
  };

  return (
    <div className="column-sum">
      <div className="column-sum__grid" style={{ ['--cells' as string]: answerCells }}>
        {/* Carry boxes: one above every column except the ones, because
            nothing is ever carried into the ones. */}
        <div className="column-sum__gutter" />
        {carries.map((value, i) => (
          <button
            key={`carry-${i}`}
            type="button"
            className="column-sum__carry"
            data-active={target.row === 'carry' && target.index === i}
            disabled={disabled}
            aria-label={`Carry box, column ${carries.length - i}`}
            onClick={() => setTarget({ row: 'carry', index: i })}
          >
            {value}
          </button>
        ))}
        <div className="column-sum__spacer" />

        <div className="column-sum__gutter" />
        {digitsOf(top).map((d, i) => (
          <div key={`top-${i}`} className="column-sum__digit">
            {d.trim()}
          </div>
        ))}

        <div className="column-sum__gutter column-sum__op">{operator}</div>
        {digitsOf(bottom).map((d, i) => (
          <div key={`bottom-${i}`} className="column-sum__digit">
            {d.trim()}
          </div>
        ))}

        <div className="column-sum__rule" />

        <div className="column-sum__gutter" />
        {answer.map((value, i) => (
          <button
            key={`answer-${i}`}
            type="button"
            className="column-sum__cell"
            data-active={target.row === 'answer' && target.index === i}
            disabled={disabled}
            aria-label={`Answer, column ${answerCells - i}`}
            onClick={() => setTarget({ row: 'answer', index: i })}
          >
            {value}
          </button>
        ))}
      </div>

      <p className="column-sum__note">
        The dotted boxes are for carrying. Nobody marks them &mdash; they&rsquo;re just your
        working.
      </p>

      <div className="keypad">
        {KEYS.map((key) => (
          <button
            key={key}
            type="button"
            className="keypad__key"
            disabled={disabled}
            onClick={() => press(key)}
          >
            {key}
          </button>
        ))}
        <button
          type="button"
          className="keypad__key keypad__key--rub"
          onClick={rub}
          disabled={disabled}
        >
          Rub out
        </button>
      </div>

      <button type="button" className="btn btn--big" disabled={!canSubmit} onClick={submit}>
        That&rsquo;s my answer
      </button>
    </div>
  );
}
